import { toLatinDigits } from "@/lib/arabic-format";

/** Half-open age range [from, to). «وما فوق» (and above) closes at 18. */
export type AgeRange = { from: number; to: number };

/** Keep a Latin range like «1–8» reading left to right inside Arabic text. */
export const ltr = (s: string) => `\u2066${s}\u2069`;

export const AGE_OPEN_END = 18;

export const AGE_CHIPS = [
  { id: "1-2", from: 1, to: 2, label: "1–2" },
  { id: "2-3", from: 2, to: 3, label: "2–3" },
  { id: "3-4", from: 3, to: 4, label: "3–4" },
  { id: "4-6", from: 4, to: 6, label: "4–6" },
  { id: "6-8", from: 6, to: 8, label: "6–8" },
  { id: "8-10", from: 8, to: 10, label: "8–10" },
] as const;

export type AgeChipId = (typeof AGE_CHIPS)[number]["id"];

export function isAgeChipId(v: unknown): v is AgeChipId {
  return typeof v === "string" && AGE_CHIPS.some((c) => c.id === v);
}

export function chipById(id: AgeChipId) {
  return AGE_CHIPS.find((c) => c.id === id)!;
}

/** «١–٣» → [1,3); «٨–١٠ وما فوق» → [8,18). Null when no number is found. */
export function parseAgeBand(band: string): AgeRange | null {
  const latin = toLatinDigits(band);
  const nums = (latin.match(/\d+(?:\.\d+)?/g) ?? []).map(Number);
  if (nums.length === 0) return null;
  const from = nums[0];
  let to = nums.length > 1 ? nums[1] : from + 1;
  if (latin.includes("وما فوق")) to = AGE_OPEN_END;
  return to > from ? { from, to } : null;
}

export function rangesOverlap(a: AgeRange, b: AgeRange): boolean {
  return a.from < b.to && b.from < a.to;
}

export function postInAgeChip(ageBand: unknown, chip: AgeRange): boolean {
  if (!Array.isArray(ageBand)) return false;
  return ageBand.some((b) => {
    const r = typeof b === "string" ? parseAgeBand(b) : null;
    return r ? rangesOverlap(r, chip) : false;
  });
}
